import { startOfDay, endOfDay } from "date-fns";
import { chargeAmountBetween } from "./chargeSchedule";
import { isPropFirmFundedStatus } from "../trading-accounts/accountStatus";

/** Source unique de vérité pour le filtrage par période du Prop Firm Manager.
 *
 *  Règles :
 *  - les bornes sont INCLUSIVES (un payout du dernier jour compte dans la période) ;
 *  - toute dépense est datée par son événement réel — le challengeCost à la création
 *    du compte, l'activationFee au passage funded — exactement comme le calendrier
 *    et le récap du mois les affichent.
 */

export interface PeriodBounds {
  start: Date | null;
  end: Date | null;
}

interface ChargeLike {
  amount: number;
  frequency: string;
  startDate?: Date | string | null;
  createdAt?: Date | string | null;
  endDate?: Date | string | null;
  billingDay?: number | null;
  accountId?: string | null;
}


interface AccountLike {
  id?: string;
  name?: string;
  status?: string | null;
  /** false = compte désactivé (cramé, supprimé côté broker) même si status vaut encore challenge/pa. */
  isActive?: boolean | null;
  createdAt?: Date | string | null;
  /** Date d'achat réelle (comptes historiques saisis a posteriori). */
  purchasedAt?: Date | string | null;
  fundedAt?: Date | string | null;
  /** Lignée challenge -> funded, posée par la transition automatique Tradovate. */
  previousAccountId?: string | null;
  challengeCost?: number | null;
  activationFee?: number | null;
  payouts?: { date: Date | string; amount: number }[];
  /** Rattachement prop firm : distingue un programme live d'un compte perso live. */
  propFirmTemplateId?: string | null;
  effectiveTemplate?: unknown;
}

/** Le compte est-il rattaché à une prop firm (directement ou via son groupe) ? */
export function hasPropFirm(account: AccountLike): boolean {
  return !!(account.effectiveTemplate ?? account.propFirmTemplateId);
}

/** Compte prop firm en cours : challenge, funded, ou programme live.
 *  Un compte cramé garde souvent son status (challenge / pa) : seul isActive=false le dit. */
export function isActivePropFirmAccount(account: AccountLike): boolean {
  if (account.isActive === false) return false;
  return account.status === "challenge" || isPropFirmFundedStatus(account.status, hasPropFirm(account));
}

/** Date à laquelle le compte a réellement été acheté.
 *  purchasedAt prime sur createdAt : ce dernier n'est que l'horodatage de saisie
 *  en base, qui vaut « aujourd'hui » pour un compte historique reconstitué. */
export function acquisitionDate(account: AccountLike): Date | null {
  if (account.purchasedAt) return new Date(account.purchasedAt);
  if (account.createdAt) return new Date(account.createdAt);
  return null;
}

/** Date du PASSAGE en funded, ou null si le compte n'est pas issu d'un passage.
 *
 *  Deux chemins produisent un compte funded, et ils datent différemment :
 *   - promotion manuelle (PromoteToFundedModal) : met à jour la ligne existante
 *     et renseigne fundedAt ;
 *   - transition automatique Tradovate : la prop firm supprime le challenge et
 *     crée un nouveau compte, donc le pipeline crée une NOUVELLE ligne Account
 *     avec previousAccountId et SANS fundedAt. La date du passage est alors la
 *     date d'acquisition de cette nouvelle ligne.
 *
 *  Un compte sans fundedAt ET sans previousAccountId n'est pas un passage : c'est
 *  un compte acheté directement en funded (pricingModel "direct_funded"). */
export function fundedDate(account: AccountLike): Date | null {
  if (account.fundedAt) return new Date(account.fundedAt);
  if (account.previousAccountId) return acquisitionDate(account);
  return null;
}

/** Normalise les bornes reçues du DateRangePicker.
 *  Une sélection manuelle renvoie minuit pour les deux bornes : sans étendre la fin
 *  à 23:59:59.999, tout ce qui tombe le dernier jour serait exclu. */
export function normalizePeriodBounds(from: Date | null | undefined, to: Date | null | undefined): PeriodBounds {
  return {
    start: from ? startOfDay(new Date(from)) : null,
    end: to ? endOfDay(new Date(to)) : null,
  };
}

/** Test d'appartenance à la période, bornes incluses. */
export function isWithinPeriod(date: Date | string, start: Date | null, end: Date | null): boolean {
  const d = new Date(date).getTime();
  if (start && d < start.getTime()) return false;
  if (end && d > end.getTime()) return false;
  return true;
}

/** Date à laquelle l'activation fee est réellement payée.
 *  fundedAt si connu, sinon createdAt pour les comptes déjà funded dont la date de
 *  passage n'a pas été enregistrée (comptes importés / historiques). */
function activationFeeDate(account: AccountLike): Date | null {
  if (account.fundedAt) return new Date(account.fundedAt);
  const isFunded = account.status === "pa" || account.status === "funded";
  if (isFunded) return acquisitionDate(account);
  return null;
}

/** Frais ponctuels d'un compte tombant dans la période. */
export function accountExpensesInPeriod(account: AccountLike, start: Date | null, end: Date | null): number {
  let total = 0;

  const challengeCost = account.challengeCost ?? 0;
  if (challengeCost > 0) {
    // Un compte sans date d'acquisition connue est considéré comme toujours compté :
    // on ne peut pas le rattacher à une période, mieux vaut ne pas perdre la dépense.
    const boughtAt = acquisitionDate(account);
    if (!boughtAt || isWithinPeriod(boughtAt, start, end)) total += challengeCost;
  }

  const activationFee = account.activationFee ?? 0;
  if (activationFee > 0) {
    const feeDate = activationFeeDate(account);
    if (feeDate && isWithinPeriod(feeDate, start, end)) total += activationFee;
  }

  return total;
}

/** Montant d'une charge récurrente ou ponctuelle imputable à la période.
 *  Délègue à chargeSchedule, source unique partagée avec Edgy et les calendriers.
 *  `sinceDate` reste le départ de repli d'une charge sans startDate ni createdAt. */
export function chargeAmountInPeriod(
  charge: ChargeLike,
  sinceDate: Date,
  start: Date | null,
  end: Date | null,
  now: Date = new Date(),
): number {
  return chargeAmountBetween(
    { ...charge, startDate: charge.startDate ?? charge.createdAt ?? sinceDate },
    start,
    end,
    now,
  );
}

export interface PfmKpis {
  totalPayouts: number;
  totalExpenses: number;
  netProfit: number;
  roi: number;
  activeCount: number;
  bestRoi: { name: string; roi: number } | null;
}

/** Agrégat des KPIs du haut du Prop Firm Manager, cohérent avec le récap du mois. */
export function computePfmKpis(
  accounts: AccountLike[],
  charges: ChargeLike[],
  from: Date | null | undefined,
  to: Date | null | undefined,
): PfmKpis {
  const { start, end } = normalizePeriodBounds(from, to);

  let totalPayouts = 0;
  let totalExpenses = 0;
  let activeCount = 0;
  let bestRoi: { name: string; roi: number } | null = null;

  for (const account of accounts) {
    const accPayouts = countsForPayouts(account)
      ? (account.payouts ?? [])
          .filter((p) => isWithinPeriod(p.date, start, end))
          .reduce((sum, p) => sum + p.amount, 0)
      : 0;
    totalPayouts += accPayouts;

    const accExpenses = countsForCosts(account)
      ? accountExpensesInPeriod(account, start, end)
      : 0;
    totalExpenses += accExpenses;

    if (isActivePropFirmAccount(account)) activeCount++;

    if (accExpenses > 0) {
      const roi = ((accPayouts - accExpenses) / accExpenses) * 100;
      if (!bestRoi || roi > bestRoi.roi) bestRoi = { name: account.name ?? "", roi };
    }
  }

  const chargeList = charges ?? [];

  for (const account of accounts) {
    if (!countsForCosts(account)) continue;
    const sinceDate = account.createdAt ? new Date(account.createdAt) : new Date();
    for (const charge of chargeList.filter((c) => c.accountId && c.accountId === account.id)) {
      totalExpenses += chargeAmountInPeriod(charge, sinceDate, start, end);
    }
  }

  const globalCharges = chargeList.filter((c) => !c.accountId);
  if (globalCharges.length > 0) {
    const createdTimes = accounts
      .map((a) => (a.createdAt ? new Date(a.createdAt).getTime() : Date.now()))
      .sort((a, b) => a - b);
    const oldest = createdTimes.length > 0 ? new Date(createdTimes[0]) : new Date();
    for (const charge of globalCharges) {
      totalExpenses += chargeAmountInPeriod(charge, oldest, start, end);
    }
  }

  const netProfit = totalPayouts - totalExpenses;
  const roi = totalExpenses > 0 ? (netProfit / totalExpenses) * 100 : 0;

  return { totalPayouts, totalExpenses, netProfit, roi, activeCount, bestRoi };
}

/** Comptes dont les PAYOUTS entrent dans la rentabilité.
 *
 *  Règle métier : un payout ne peut venir que d'un compte funded ou live —
 *  on ne retire pas d'argent d'un challenge. Un compte désactivé qui a
 *  encaissé des payouts par le passé reste compté : l'argent a bien été reçu.
 *
 *  Un compte de saisie historique laissé au statut par défaut ("challenge")
 *  est donc EXCLU. C'est délibéré : le statut est la seule donnée dont on
 *  dispose, et l'inclure ferait diverger cette vue de toutes les autres. */
export function countsForPayouts(account: AccountLike): boolean {
  if (account.status === "pa" || account.status === "live") return true;
  return account.status === "inactive" && (account.payouts?.length ?? 0) > 0;
}

/** Comptes dont les COÛTS entrent dans la rentabilité : TOUS.
 *
 *  Règle métier (tranchée le 2026-09-21) : dès qu'un compte a coûté de
 *  l'argent, ce coût pèse — quel que soit son statut et quoi qu'il ait
 *  rapporté. Un challenge cramé (`inactive`, zéro payout) a bien été payé ;
 *  un compte `demo` de prop firm est payant lui aussi.
 *
 *  L'ancienne version listait des statuts autorisés, et ce filtrage était la
 *  cause du bug remonté : le calendrier affichait 70 $ de frais sur un mois
 *  que la barre KPI chiffrait à 36 $ sur une période PLUS LARGE, parce que
 *  les comptes cramés — le cas le plus fréquent — étaient silencieusement
 *  écartés du total. Les deux vues lisaient les mêmes comptes avec deux
 *  règles différentes.
 *
 *  Ne pas réintroduire de liste blanche de statuts ici : le montant réel
 *  (challengeCost / activationFee / charges) est la seule condition, et il
 *  est déjà nul pour un compte qui n'a rien coûté. Un statut absent de la
 *  liste redeviendrait une dépense invisible.
 *
 *  Le pendant côté recettes, countsForPayouts, reste volontairement STRICT :
 *  on ne retire pas d'argent d'un challenge. Les deux règles ne sont pas
 *  symétriques, et c'est voulu. */
export function countsForCosts(_account: AccountLike): boolean {
  return true;
}
